export function parseTimestamp(ts) {
  if (!ts) return new Date();
  const date = new Date(ts);
  if (!Number.isNaN(date.getTime())) return date;
  const today = new Date();
  const [hours, minutes] = String(ts).split(':');
  today.setHours(Number(hours) || 0, Number(minutes) || 0, 0, 0);
  return today;
}

/** True when timestamp is a full date (ISO), not time-only HH:MM. */
export function hasFullDateTimestamp(ts) {
  if (ts == null || ts === '') return false;
  const raw = String(ts);
  if (/^\d{1,2}:\d{2}/.test(raw) && !raw.includes('T') && raw.length <= 8) {
    return false;
  }
  const date = new Date(raw);
  return !Number.isNaN(date.getTime());
}

export function formatDayLabel(ts, now = new Date()) {
  if (!hasFullDateTimestamp(ts)) return null;
  const date = parseTimestamp(ts);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfMsg = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((startOfToday - startOfMsg) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year:
      date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
  });
}

/**
 * Build Virtuoso rows: optional day separators + messages.
 * Separators only when messages have full-date timestamps.
 */
export function buildTimelineItems(messages = [], now = new Date()) {
  const items = [];
  let lastDayKey = null;
  const canSeparate = messages.some((m) => hasFullDateTimestamp(m.timestamp));

  messages.forEach((msg) => {
    if (canSeparate && hasFullDateTimestamp(msg.timestamp)) {
      const d = parseTimestamp(msg.timestamp);
      const dayKey = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (dayKey !== lastDayKey) {
        lastDayKey = dayKey;
        items.push({
          type: 'separator',
          key: `sep-${dayKey}`,
          label: formatDayLabel(msg.timestamp, now),
        });
      }
    }
    items.push({ type: 'message', key: msg.id ?? msg.localId, message: msg });
  });

  return items;
}

export function normalizeQuickReplies(raw) {
  if (!Array.isArray(raw) || raw.length === 0) return [];

  return raw
    .map((item) => {
      if (typeof item === 'string') return item.trim();
      if (item && typeof item === 'object') {
        const label =
          item.title || item.text || item.label || item.payload || item.name;
        return label != null ? String(label).trim() : '';
      }
      return '';
    })
    .filter(Boolean);
}

/**
 * Active action buttons come from the latest bot turn (consecutive bot
 * messages since the last user message). Prefer the most recent message
 * that actually carries quick replies — later media-only bubbles must not
 * clear earlier actions.
 */
export function getActiveQuickReplies(messages = []) {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (msg.is_from_user) {
      return [];
    }
    if (msg.kind === 'reminder') {
      continue;
    }
    const replies = normalizeQuickReplies(msg.quick_replies);
    if (replies.length > 0) {
      return replies;
    }
  }
  return [];
}

const MAX_ENUMERATED_OPTIONS = 6;
const MAX_ENUMERATED_LABEL = 80;
const ENUMERATED_LINE = /^\s*(\d{1,2})[.)]\s+(.+?)\s*$/;

/**
 * Quiz flows send their options as plain numbered lines in the message body
 * ("1. The right one"), not as quick_replies — so the answer deck never
 * appeared and learners typed the number by hand. This extracts those lines
 * into deck options that send the number, exactly as typing would.
 *
 * The heuristic is deliberately strict so ordinary numbered instructions don't
 * become buttons: numbering must start at 1, be consecutive, and yield 2-6
 * short options.
 */
export function extractEnumeratedOptions(content) {
  if (!content || typeof content !== 'string') return [];

  const lines = content
    .split(/<br\s*\/?>|\n/i)
    .map((line) => line.replace(/<[^>]*>/g, '').trim());

  const options = [];
  for (const line of lines) {
    const match = ENUMERATED_LINE.exec(line);
    if (!match) {
      // A non-option line between options breaks the run; keep the longest
      // completed run rather than stitching separate lists together.
      if (options.length >= 2) break;
      options.length = 0;
      continue;
    }
    const number = parseInt(match[1], 10);
    const label = match[2];
    if (number !== options.length + 1) {
      // Restart if this line begins a fresh list.
      options.length = 0;
      if (number !== 1) continue;
    }
    if (label.length > MAX_ENUMERATED_LABEL) {
      options.length = 0;
      continue;
    }
    options.push({ key: String(number), label, send: String(number) });
  }

  if (options.length < 2 || options.length > MAX_ENUMERATED_OPTIONS) return [];
  return options;
}

/**
 * What the answer deck should offer right now. Real quick replies always win;
 * otherwise fall back to options extracted from the active bot turn, using the
 * same walk as getActiveQuickReplies so an already-answered question never
 * re-offers its options.
 */
export function getActiveAnswerOptions(messages = [], liveQuickReplies = []) {
  const real = normalizeQuickReplies(liveQuickReplies);
  if (real.length > 0) {
    return real.map((reply) => ({ key: null, label: reply, send: reply }));
  }

  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (msg.is_from_user) {
      return [];
    }
    if (msg.kind === 'reminder') {
      continue;
    }
    const replies = normalizeQuickReplies(msg.quick_replies);
    if (replies.length > 0) {
      return replies.map((reply) => ({ key: null, label: reply, send: reply }));
    }
    const extracted = extractEnumeratedOptions(msg.content);
    if (extracted.length > 0) {
      return extracted;
    }
  }
  return [];
}

export function normalizeMessage(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (raw.type === 'separator') return null;

  const content = raw.content ?? raw.text;
  if (content == null && !raw.image) return null;

  const kind = raw.kind === 'reminder' ? 'reminder' : 'chat';

  return {
    id: raw.id ?? undefined,
    localId: raw.localId,
    username: raw.username || 'Bot',
    content: content == null ? '' : String(content),
    is_from_user: !!raw.is_from_user,
    kind,
    quick_replies: normalizeQuickReplies(raw.quick_replies),
    image: raw.image,
    timestamp: raw.timestamp || new Date().toISOString(),
    status: raw.status || (raw.id != null ? 'sent' : undefined),
  };
}

export function parseReminderContent(content) {
  const text = String(content || '').trim();
  const breakIndex = text.indexOf('\n');
  if (breakIndex === -1) {
    return { title: text, body: '' };
  }
  return {
    title: text.slice(0, breakIndex).trim(),
    body: text.slice(breakIndex + 1).trim(),
  };
}

export function sortMessages(messages) {
  return [...messages].sort((a, b) => {
    const timeDiff =
      parseTimestamp(a.timestamp).getTime() -
      parseTimestamp(b.timestamp).getTime();
    if (timeDiff !== 0) return timeDiff;
    if (a.id != null && b.id != null) return Number(a.id) - Number(b.id);
    return 0;
  });
}

function messageContent(msg) {
  return msg?.content ?? msg?.text ?? '';
}

/** Only treat an optimistic bubble as a server echo within this window */
const OPTIMISTIC_MATCH_WINDOW_MS = 2 * 60 * 1000;

function isNearTimestamp(a, b, windowMs = OPTIMISTIC_MATCH_WINDOW_MS) {
  return (
    Math.abs(parseTimestamp(a).getTime() - parseTimestamp(b).getTime()) <=
    windowMs
  );
}

export function mergeMessages(existing = [], incoming = []) {
  const byId = new Map();
  const withoutId = [];
  const incomingIds = new Set();

  const ingest = (msg, fromIncoming = false) => {
    const normalized = normalizeMessage(msg);
    if (!normalized) return;
    if (normalized.id != null) {
      if (fromIncoming) incomingIds.add(normalized.id);
      const prev = byId.get(normalized.id) || {};
      byId.set(normalized.id, {
        ...prev,
        ...normalized,
        // Don't let an empty update wipe actions already attached to this message
        quick_replies:
          normalized.quick_replies.length > 0
            ? normalized.quick_replies
            : prev.quick_replies || [],
        status: normalized.status || prev.status || 'sent',
      });
    } else {
      withoutId.push(normalized);
    }
  };

  existing.forEach((msg) => ingest(msg, false));
  incoming.forEach((msg) => ingest(msg, true));

  const serverMessages = [...byId.values()];
  const matchedServerIds = new Set();
  const keptOptimistic = [];

  withoutId.forEach((opt) => {
    // Only dedupe against server rows that arrived in *this* incoming batch and
    // look like the same send (content + side + near timestamp). Otherwise a
    // repeated quiz answer like "1" gets collapsed into an older history bubble.
    const match = serverMessages.find(
      (server) =>
        !matchedServerIds.has(server.id) &&
        incomingIds.has(server.id) &&
        server.is_from_user === opt.is_from_user &&
        messageContent(server) === messageContent(opt) &&
        isNearTimestamp(server.timestamp, opt.timestamp)
    );

    if (match) {
      matchedServerIds.add(match.id);
      byId.set(match.id, {
        ...match,
        localId: opt.localId || match.localId,
        status: opt.status === 'pending' ? 'sent' : opt.status || match.status,
      });
      return;
    }

    keptOptimistic.push(opt);
  });

  return sortMessages([...byId.values(), ...keptOptimistic]);
}

export function createLocalMessage({
  username,
  content,
  is_from_user = true,
  status = 'pending',
}) {
  return {
    localId: `local-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    username,
    content,
    is_from_user,
    timestamp: new Date().toISOString(),
    quick_replies: [],
    status,
  };
}

export function updateMessageStatus(messages, localId, status) {
  return messages.map((msg) =>
    msg.localId === localId ? { ...msg, status } : msg
  );
}

export function draftStorageKey({ channelId, channelName, username }) {
  return `webchat-draft:${channelId}:${channelName}:${username || ''}`;
}
